/**
 * Jain Gaurav Yatra — Certificates (eligible members).
 * Backend: GET /jain_gaurav_yatra/certificates/eligible/  (cookie-session auth + X-Tenant-ID).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg = pick(d?.message) ?? pick(d?.detail);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const ELIGIBLE_PATH = "/jain_gaurav_yatra/certificates/eligible/";
const ISSUED_PATH = "/jain_gaurav_yatra/certificates/issued/";

/** A member eligible to receive (or who already has) a completion certificate. */
export interface EligibleMember {
  id: string;
  full_name: string | null;
  phone: string | null;
  visited: number;
  total: number;
  already_issued: boolean;
  certificate_number: string | null;
  issued_at: string | null;
  certificate_url: string | null;
}

function numOr(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === "") {return null;}
  return String(v);
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

/**
 * Reads visited/total. Tolerates several backend shapes:
 *  - a combined "1/10" string on `progress` (eligible) or `visited` (issued),
 *  - separate numeric fields: `completed`/`total`, `visited_count`/`total_temples`,
 *  - a nested `progress: { visited, total }` object.
 */
function parseProgress(row: Record<string, unknown>): { visited: number; total: number } {
  for (const key of ["progress", "visited"] as const) {
    const v = row[key];
    if (typeof v === "string" && v.includes("/")) {
      const [a, b] = v.split("/").map((s) => Number(s.trim()));
      return { visited: Number.isFinite(a) ? a : 0, total: Number.isFinite(b) ? b : 0 };
    }
  }
  const progress = obj(row.progress);
  return {
    visited: numOr(progress.visited ?? row.completed ?? row.visited_count ?? row.visited, 0),
    total: numOr(progress.total ?? row.total ?? row.total_temples, 0),
  };
}

function toEligible(row: Record<string, unknown>): EligibleMember {
  const certificate = obj(row.certificate);
  const { visited, total } = parseProgress(row);
  const certNumber =
    strOrNull(certificate.certificate_number) ??
    strOrNull(certificate.number) ??
    strOrNull(row.certificate_number);
  return {
    // Eligible rows key the member by `user_id`; other shapes use `id`.
    id: String(row.user_id ?? row.id ?? ""),
    full_name: strOrNull(row.full_name) ?? strOrNull(row.name),
    phone: strOrNull(row.phone),
    visited,
    total,
    already_issued: row.already_issued === true || !!certNumber,
    certificate_number: certNumber,
    issued_at: strOrNull(certificate.issued_at) ?? strOrNull(row.issued_at),
    certificate_url: strOrNull(certificate.certificate_url) ?? strOrNull(row.certificate_url),
  };
}

export async function listEligibleMembers(): Promise<EligibleMember[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${ELIGIBLE_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toEligible);
}

/** An already-issued certificate. */
export interface IssuedCertificate {
  id: string;
  certificate_number: string;
  member_name: string;
  issued_at: string;
  visited: number;
  total: number;
  certificate_url: string | null;
}

function toIssued(row: Record<string, unknown>): IssuedCertificate {
  const member = obj(row.member ?? row.user);
  const certificate = obj(row.certificate);
  const { visited, total } = parseProgress(row);
  return {
    id: String(row.id ?? ""),
    certificate_number: String(
      certificate.certificate_number ?? row.certificate_number ?? row.number ?? "",
    ),
    member_name:
      strOrNull(row.member_name) ??
      strOrNull(member.full_name) ??
      strOrNull(row.full_name) ??
      strOrNull(row.name) ??
      "Yatri",
    issued_at: String(certificate.issued_at ?? row.issued_at ?? ""),
    visited,
    total,
    certificate_url: strOrNull(certificate.certificate_url) ?? strOrNull(row.certificate_url),
  };
}

export async function listIssuedCertificates(): Promise<IssuedCertificate[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${ISSUED_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toIssued);
}

/**
 * Fetches a certificate PDF from its backend URL (with tenant + auth headers,
 * which a plain link can't send) and triggers a browser download.
 */
export async function downloadCertificatePdf(url: string, filename = "certificate.pdf"): Promise<void> {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      ...(id ? { "X-Tenant-ID": id } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Failed to download certificate (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
